import { api } from "services/restUtil";
import { defineApiQuery } from "utils/queries";
import S3LibraryStore from "./S3LibraryStore";
import LibraryVersionService from "./LibraryVersionService";
import type { FolderContents } from "./model";
import type {
  AdminLibraryObject,
  LibraryObject,
  LibraryObjectType,
  LibraryVersion,
  WorkshopListResponse,
} from "./model";
import type { Tag } from "services/tags/model";

export const s3LibraryStore = new S3LibraryStore();
export const libraryVersionService = new LibraryVersionService();

export function fetchLibraryObjects(): Promise<LibraryObject[]> {
  return api.get<LibraryObject[]>("/library");
}

// DEPRECATED Use featuredContentQuery instead
export function fetchFeaturedLibraryObjects(): Promise<LibraryObject[]> {
  return api.get<LibraryObject[]>("/library/featured");
}

type FeaturedContentResponse = { list: LibraryObject[]; total: number };
export const featuredContentQuery = defineApiQuery<FeaturedContentResponse>({
  key: "/library/featured",
});

type ContentUsageParams = { category: string; from?: string; to?: string };
type ContentUsageResponse = {
  list: Array<{ name: string; count: number; value?: number }>;
};

export function fetchContentUsage(
  params: ContentUsageParams
): Promise<ContentUsageResponse> {
  return api.get<ContentUsageResponse>("/library/usage", params);
}

export const workshopsQuery = defineApiQuery<WorkshopListResponse>({
  key: "/workshops",
  queryFn: () => fetchWorkshops(),
});

type WorkshopListParams = {
  page?: number;
  size?: number;
};

export function fetchWorkshops(params?: WorkshopListParams) {
  return api.get<WorkshopListResponse>("/workshops", params ?? null);
}

type LibraryListResponse = { list: LibraryObject[]; total: number };

type ContentListParams = {
  type: Extract<LibraryObjectType, "lesson" | "course">;
  page?: number; // 1-based
  page_size?: number;
};

export function fetchInProgressContent(
  params: ContentListParams
): Promise<LibraryListResponse> {
  return api.get<LibraryListResponse>("/library/started", params);
}

export const fetchStartedContent = defineApiQuery({
  key: "/library/started",
});

export function fetchCompletedContent(
  params: ContentListParams
): Promise<LibraryListResponse> {
  return api.get<LibraryListResponse>("/library/completed", params);
}

// GET /library/favorite querystring, per the server's FavoritesLibraryObjectRequest.
// Note: no `type` filter — the server returns all favorites regardless of object type.
type FavoriteContentParams = {
  sort_by?: "key" | "date";
  order?: "asc" | "desc";
  page?: number; // 1-based
  page_size?: number;
};

export function fetchFavoriteContent(
  params?: FavoriteContentParams
): Promise<LibraryListResponse> {
  return api.get<LibraryListResponse>("/library/favorite", params);
}

export function fetchLibraryObject(args: { id: string }): Promise<LibraryObject> {
  return api.get<LibraryObject>(`/library/${args.id}`);
}

type TagsResponse = { list: Tag[]; total: number };

// GET /tags querystring, per the server's GetTagsSchema (+ PageSchema).
type FetchContentTagsParams = {
  name?: string;
  category?: string; // comma-separated category names.
  page?: number; // 1-based
  size?: number;
};

export function fetchTags(params?: FetchContentTagsParams): Promise<TagsResponse> {
  return api.get<TagsResponse>("/tags", params);
}

type CourseSearchParams = {
  page?: number;
  size?: number;
  query?: string;
  sort_by?: string;
  include_lesson?: boolean;
  include_course?: boolean;
  tags?: string;
};

export function searchCourse(params: CourseSearchParams): Promise<LibraryListResponse> {
  return api.get<LibraryListResponse>("/library/course/search", params);
}

export async function fetchFileContentBody(args: { id: string }): Promise<Document> {
  const resp = await api.get<Response>(`/library/${args.id}/content`, undefined, {
    rawResponse: true,
    headers: { Accept: "application/xml, text/html" },
  });
  const contentType = resp.headers.get("content-type") ?? "";
  if (!contentType.includes("html") && !contentType.includes("xml")) {
    throw new Error(
      "Unfortunately we don’t support this content type yet but we’ll add it very soon."
    );
  }
  const text = await resp.text();
  const mimeTime = contentType.includes("xml") ? "text/xml" : "text/html";
  return new DOMParser().parseFromString(text, mimeTime);
}

type TagPayload = { tags: string[] };

export function addLibraryObjectTags(args: { id: string; tags: string[] }) {
  return api.post<TagPayload, void>(`/library/${args.id}/tags`, { tags: args.tags });
}

export function removeLibraryObjectTags(args: { id: string; tags: string[] }) {
  return api.delete<TagPayload, void>(`/library/${args.id}/tags`, { tags: args.tags });
}

type ExercisesResponse = { list: Array<Record<string, unknown>>; total: number };

export function fetchExercisesBySource(args: {
  id: string;
  page?: number;
  size?: number;
}): Promise<ExercisesResponse> {
  return api.get<ExercisesResponse>(`/library/${args.id}/exercises`, {
    page: args.page,
    size: args.size,
  });
}

export function createFolder(newObject: LibraryObject, parentKey: string) {
  return s3LibraryStore.createFolder(newObject, parentKey);
}

export function updateFolderMetadata(object: LibraryObject) {
  return s3LibraryStore.updateFolderMetadata(object);
}

export function renameFolder(object: LibraryObject) {
  return s3LibraryStore.renameFolder(object);
}

export function moveFolder(oldKey: string, newKey: string) {
  return s3LibraryStore.moveFolder(oldKey, newKey);
}

export function createFile(
  newObject: LibraryObject,
  parentKey: string,
  contentFiles: globalThis.File[],
  assetFiles?: globalThis.File[]
) {
  return s3LibraryStore.createFile(newObject, parentKey, contentFiles, assetFiles ?? []);
}

export function updateFile(
  parentKey: string,
  contentFiles: globalThis.File[],
  assetFiles: globalThis.File[]
) {
  return s3LibraryStore.updateFile(parentKey, contentFiles, assetFiles);
}

type FolderContentResponse = FolderContents & {
  libraryObjects?: Record<string, LibraryObject>;
};

export function listFolderContents(args: { key: string; maxNumberOfKeys?: number }) {
  return s3LibraryStore.listFolderContents(args.key, args.maxNumberOfKeys);
}

type FolderWithLibraryObjectsArgs = {
  key: string;
  libraryObjects?: Record<string, LibraryObject>;
  maxNumberOfKeys?: number;
};

export async function listFolderContentsEnriched(
  args: FolderWithLibraryObjectsArgs
): Promise<FolderContentResponse> {
  const folderResponse: FolderContentResponse = await s3LibraryStore.listFolderContents(
    args.key
  );

  const applyLibraryObjects = (
    libraryObjects: Record<string, LibraryObject>
  ): FolderContentResponse => {
    folderResponse.libraryObjects = libraryObjects;
    for (const folder of folderResponse.folders) {
      folder.libraryObject = libraryObjects[folder.key];
    }
    for (const file of folderResponse.files) {
      file.libraryObject = libraryObjects[file.key];
    }
    return folderResponse;
  };

  if (args.libraryObjects) {
    return applyLibraryObjects(args.libraryObjects);
  }

  try {
    const list = await fetchLibraryObjects();
    const libraryObjects: Record<string, LibraryObject> = {};
    for (const obj of list) {
      libraryObjects[obj.key] = obj;
    }
    return applyLibraryObjects(libraryObjects);
  } catch {
    return folderResponse;
  }
}

export function removeFolder(args: { key: string }) {
  return s3LibraryStore.removeFolder(args.key);
}

export function fetchObjectMetadata(key: string) {
  return s3LibraryStore.fetchObjectMetadata(key);
}

export const makeAssetFileURL = (key: string) => (fileName: string) =>
  libraryVersionService.getSignedResourceURL(key, fileName);

type LibraryVersionAndFile = { file: unknown; version: LibraryVersion };

export function fetchVersionWithEditor(args: {
  libraryVersionId: string;
}): Promise<LibraryVersionAndFile> {
  const service = libraryVersionService;
  return Promise.all([
    service.fetchEditorFile(args.libraryVersionId),
    service.fetchVersion(args.libraryVersionId),
  ]).then(([file, version]) => ({ file, version }));
}

type LibraryObjectAndFile = { file: unknown; object: AdminLibraryObject };

export function fetchObjectWithEditor(args: {
  libraryObjectId: string;
}): Promise<LibraryObjectAndFile> {
  const service = libraryVersionService;
  return Promise.all([
    service.fetchEditorFile(undefined, args.libraryObjectId),
    service.fetchObject(args.libraryObjectId),
  ]).then(([file, object]) => ({ file, object }));
}

export function clearContentCredentials() {
  s3LibraryStore.clearCredentials();
}

export * from "./utils";
